import { useAgents } from '#~/agent/hooks.js';
import { Protocol } from '#~/video/components/protocol/index.js';
import { Column, Group } from '@studio/design-system/form';
import { AnimatePresence, motion, Motions } from '@studio/design-system/motion';
import type { HtmlAttributes } from '@studio/design-system/props';
import { ensureNotNil } from '@studio/js-utils';
import { Protocol as M_Protocol } from '@studio/models/video';
import { toAccessor } from '@studio/react-utils';
import { useLatestFn } from '@studio/react-utils/hooks';
import { clsx } from 'cn';
import type { ComponentType, ReactElement } from 'react';
import { createElement } from 'react';
import { shallow } from 'zustand/shallow';
// Mount <AutoConfiguredLabel> on the Video protocol Group via the
// design-system `annotation` slot (Multi-Add Phase 2). The protocol
// value covers all three add paths: known-model template, ONVIF probe
// success, and NDI discovery (all record `video.protocol`). Stream URL
// labelling lives in rtsp.tsx (sibling); NDI's video.host is
// intentionally not labelled because NDI uses the discovery's
// host/name verbatim with no template override.
import { AutoConfiguredLabel } from '../auto-configured-label.jsx';
import { useProducer, useStore } from '../store/hooks.js';
import { Local } from './local.jsx';
import { Ndi } from './ndi.jsx';
import { Rtsp } from './rtsp.jsx';

const Components: Record<M_Protocol, ComponentType<HtmlAttributes>> = {
  [M_Protocol.NDI]: Ndi,
  [M_Protocol.RTSP]: Rtsp,
  [M_Protocol.RTSPT]: Rtsp,
  [M_Protocol.RTSPLT]: Rtsp,
  [M_Protocol.Local]: Local,
  [M_Protocol.IRIS]: Local
};

/** `<Settings>` component. */
export const Settings = (props: HtmlAttributes): ReactElement => {
  const producer = useProducer();
  // `candidateId` is read alongside the existing protocol/agentId
  // pair so the `<AutoConfiguredLabel>` mount below can
  // address the depot slice keyed by `(deviceId, fieldPath)`.
  const { protocol, agentId, candidateId } = useStore(
    (µ) => ({
      protocol: ensureNotNil(µ.candidate.video?.protocol),
      agentId: µ.agentId,
      candidateId: µ.candidate.id
    }),
    shallow
  );

  const handleProtocolChange = useLatestFn((nextProtocol: M_Protocol) => {
    producer(({ candidate, video }) => {
      const { cache } = video;

      // Cache the current video settings.
      cache[protocol] = candidate.video;

      // Restore the saved video settings (if applicable)
      const isRtspVideo = candidate.video;
      if (nextProtocol === M_Protocol.NDI && isRtspVideo?.protocol === M_Protocol.RTSP) {
        // Switching from RTSP to NDI lacks of critical information in the
        // candidate device like host and name to create a valid NDI
        // configuration -> a valid ndi device in the encoder.
        const host = isRtspVideo?.host;
        const name = candidate.name;
        candidate.video = {
          ...cache[nextProtocol],
          protocol: nextProtocol,
          host: host,
          name: name
        };
      } else {
        candidate.video = { ...cache[nextProtocol], protocol: nextProtocol };
      }
    });
  });
  const agent = useAgents((a) => a.find((a_) => a_.id === agentId), shallow);

  return (
    <Column
      {...props}
      className={clsx({
        ['pointer-events-none']: agent?.embedded
      })}
    >
      {protocol !== M_Protocol.Local && (
        /* Video protocol Group surfaces "Auto Configured" when the
            slice records `(candidateId, 'video.protocol')`. Field path
            matches the value recorded by the multi-add and single-add
            recording paths. The live `value` is the protocol enum
            stringified — passed verbatim so the label's value-
            comparison eviction stays accurate. */
        <Group
          annotation={
            <AutoConfiguredLabel
              deviceId={candidateId}
              fieldPath='video.protocol'
              value={protocol}
            />
          }
          label='Video protocol'
        >
          <Protocol
            disabled={agent?.embedded}
            protocol={toAccessor(protocol, handleProtocolChange)}
          />
        </Group>
      )}

      <AnimatePresence>
        <motion.div key={protocol} {...Motions.Fade}>
          {createElement(Components[protocol])}
        </motion.div>
      </AnimatePresence>

      {/* <Group label='Invert source'>
        <Switch.Container label='Enable if mounted upside down'>
          <Switch.Archetype disabled />
        </Switch.Container>
      </Group> */}
    </Column>
  );
};
